import {DateTime, Interval} from "luxon";
import {container, inject, singleton} from "tsyringe";
import {BackendClient} from "../../connectivity/backend-client";
import {persistence} from "../../decorators/persistence.decorator";
import {BoardType} from "../../enums/board-type.enum";
import {ArrayHelper} from "../../helpers/array.helper";
import {DateHelper} from "../../helpers/date.helper";
import {TransformerHelper} from "../../helpers/transformer.helper";
import {Repository} from "../../persistence/repository";
import {ProjectDTO} from "../dtos/project.dto";
import {DummyProject} from "../entities/dummy/dummy-project.entity";
import {Employee} from "../entities/employee.entity";
import {Project} from "../entities/project.entity";
import {IssueRepository} from "./issue.repository";
import {ProjectTypeRepository} from "./project-type.repository";
import {appState} from "../../app.state";
import {BOARD_TYPE_DAILY} from "../../constants";
import {Issue} from "../entities/issue.entity";

@singleton()
@persistence({
    entity: Project,
    preload: true
})
export class ProjectRepository extends Repository<Project> {


    constructor(
        @inject(BackendClient) private readonly backendClient: BackendClient
    ) {
        super();
    }

    private static _dummy = new DummyProject(ProjectTypeRepository.dummy);

    public static get dummy(): DummyProject {
        return this._dummy;
    }

    public async create(boardType: string = BOARD_TYPE_DAILY): Promise<Project | null> {
        const projectType = await container.resolve<ProjectTypeRepository>(ProjectTypeRepository).findOneById(boardType);
        if (!projectType) {
            return null;
        }

        const project = new Project(projectType);
        project.title = ``;
        // null, not '': this seed reaches the database untouched whenever a project or a
        // daily is created without anyone opening the URL field, so an empty string here
        // re-creates the very rows the migration cleared.
        project.url = null;
        project.description = ``;
        project.color = `#B1B1B1`;
        project.creator = await appState.employee;

        return project;
    }

    public async findAllByIntervalAndType(interval: Interval, pointer: DateTime, types?: string[], employee?: Employee, includes?: string[]): Promise<Project[]> {
        const queryParams: Record<string, string | string[]> = {
            start: interval.start!.toSQLDate()!,
            end: interval.end!.toSQLDate()!,
        };

        if (types) {
            queryParams.types = types;
        }

        if (employee) {
            queryParams.employee = employee.uid;
        }

        if (includes) {
            queryParams.includes = includes;
        }

        const projectDTOs = await this.backendClient.get<ProjectDTO[]>(
            `/api/v1/query/project-by-interval`,
            false,
            queryParams
        );

        return (await Promise.all(
            (projectDTOs ?? []).map<Promise<Project>>(async dto => {
                const project = await TransformerHelper.fromDTO(dto, Project);
                const boardTypesWithoutDelta: string[] = [BoardType.DAILY_WORK, BoardType.AGGREGATION];
                if (!boardTypesWithoutDelta.includes(project.type.uid)) {
                    project.sorting = DateHelper.calculateDelta(project, pointer);
                }
                project.issues = (await Promise.all((dto.issues ?? []).map(issue => container.resolve(IssueRepository).findOneByIdOrDto(issue)))).filter(ArrayHelper.notEmpty<Issue>);

                return project;
            })
        )).sort(
            (projectA, projectB) => {
                if (projectA.type.uid === BoardType.DAILY_WORK && projectB.type.uid === BoardType.DAILY_WORK) {
                    return (projectA.sorting === projectB.sorting)
                        ? projectA.title.localeCompare(projectB.title)
                        : projectA.sorting - projectB.sorting;
                }


                if (projectA.type.uid === BoardType.DAILY_WORK && projectB.type.uid !== BoardType.DAILY_WORK) {
                    return -1;
                }

                if (projectA.type.uid !== BoardType.DAILY_WORK && projectB.type.uid === BoardType.DAILY_WORK) {
                    return 1;
                }

                if (projectA.type.uid === BoardType.AGGREGATION && projectB.type.uid === BoardType.AGGREGATION) {
                    return (projectA.sorting === projectB.sorting)
                        ? projectA.title.localeCompare(projectB.title)
                        : projectA.sorting - projectB.sorting;
                }

                if (projectA.type.uid === BoardType.AGGREGATION && projectB.type.uid !== BoardType.AGGREGATION) {
                    return -1;
                }

                if (projectA.type.uid !== BoardType.AGGREGATION && projectB.type.uid === BoardType.AGGREGATION) {
                    return 1;
                }

                return projectA.sorting - projectB.sorting;
            }
        );
    }

    public async hasIssues(uid: string): Promise<boolean> {
        const response = await this.backendClient.get<boolean>(
            `/api/v1/query/has-board-issues`,
            false,
            {uid}
        );

        return response ?? true;
    }
}